import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface Box3DProps {
  length: number;
  width: number;
  height: number;
  unit?: string;
  label?: string;
  showGrid?: boolean;
  mode?: 'stimulus' | 'support';
}
export function Box3D({
  length,
  width,
  height,
  unit = 'cm',
  label,
  showGrid = true,
  mode = 'stimulus'
}: Box3DProps) {
  const {
    svgWidth,
    svgHeight,
    corners,
    paths,
    gridLines,
    labels
  } = useMemo(() => {
    const maxDim = Math.max(length, width, height);
    const scale = Math.min(30, Math.max(8, 200 / maxDim));
    const isoAngle = Math.PI / 6;
    const xStep = scale * Math.cos(isoAngle);
    const yStep = scale * Math.sin(isoAngle);
    const zStep = scale;
    const projectedWidth = (length + width) * xStep;
    const projectedHeight = (length + width) * yStep + height * zStep;
    const padding = 60;
    const w = Math.max(320, projectedWidth + padding * 2);
    const h = Math.max(240, projectedHeight + padding * 2);
    const startX = (w - projectedWidth) / 2 + width * xStep;
    const startY = h - padding;
    const pts = {
      origin: {
        x: startX,
        y: startY
      },
      frontBottomRight: {
        x: startX + length * xStep,
        y: startY - length * yStep
      },
      backBottomLeft: {
        x: startX - width * xStep,
        y: startY - width * yStep
      },
      backBottomRight: {
        x: startX + (length - width) * xStep,
        y: startY - (length + width) * yStep
      },
      originTop: {
        x: startX,
        y: startY - height * zStep
      },
      frontTopRight: {
        x: startX + length * xStep,
        y: startY - length * yStep - height * zStep
      },
      backTopLeft: {
        x: startX - width * xStep,
        y: startY - width * yStep - height * zStep
      },
      backTopRight: {
        x: startX + (length - width) * xStep,
        y: startY - (length + width) * yStep - height * zStep
      }
    };
    const facePaths = {
      front: `M ${pts.origin.x},${pts.origin.y} L ${pts.frontBottomRight.x},${pts.frontBottomRight.y} L ${pts.frontTopRight.x},${pts.frontTopRight.y} L ${pts.originTop.x},${pts.originTop.y} Z`,
      top: `M ${pts.originTop.x},${pts.originTop.y} L ${pts.frontTopRight.x},${pts.frontTopRight.y} L ${pts.backTopRight.x},${pts.backTopRight.y} L ${pts.backTopLeft.x},${pts.backTopLeft.y} Z`,
      right: `M ${pts.frontBottomRight.x},${pts.frontBottomRight.y} L ${pts.backBottomRight.x},${pts.backBottomRight.y} L ${pts.backTopRight.x},${pts.backTopRight.y} L ${pts.frontTopRight.x},${pts.frontTopRight.y} Z`
    };
    const grids: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      key: string;
    }[] = [];
    if (showGrid) {
      for (let i = 1; i < length; i++) {
        grids.push({
          x1: pts.origin.x + i * xStep,
          y1: pts.origin.y - i * yStep,
          x2: pts.origin.x + i * xStep,
          y2: pts.originTop.y - i * yStep,
          key: `f-v-${i}`
        });
      }
      for (let i = 1; i < height; i++) {
        grids.push({
          x1: pts.origin.x,
          y1: pts.origin.y - i * zStep,
          x2: pts.frontBottomRight.x,
          y2: pts.frontBottomRight.y - i * zStep,
          key: `f-h-${i}`
        });
      }
      for (let i = 1; i < length; i++) {
        grids.push({
          x1: pts.originTop.x + i * xStep,
          y1: pts.originTop.y - i * yStep,
          x2: pts.backTopLeft.x + i * xStep,
          y2: pts.backTopLeft.y - i * yStep,
          key: `t-v-${i}`
        });
      }
      for (let i = 1; i < width; i++) {
        grids.push({
          x1: pts.originTop.x - i * xStep,
          y1: pts.originTop.y - i * yStep,
          x2: pts.frontTopRight.x - i * xStep,
          y2: pts.frontTopRight.y - i * yStep,
          key: `t-h-${i}`
        });
      }
    }
    return {
      svgWidth: w,
      svgHeight: h,
      corners: pts,
      paths: facePaths,
      gridLines: grids,
      labels: {
        length: {
          x: (pts.origin.x + pts.frontBottomRight.x) / 2 + 10,
          y: (pts.origin.y + pts.frontBottomRight.y) / 2 + 25
        },
        width: {
          x: (pts.frontBottomRight.x + pts.backBottomRight.x) / 2 + 25,
          y: (pts.frontBottomRight.y + pts.backBottomRight.y) / 2
        },
        height: {
          x: pts.origin.x - 15,
          y: (pts.origin.y + pts.originTop.y) / 2
        }
      }
    };
  }, [length, width, height, showGrid]);
  return <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm inline-block" data-id="element-8941">
      {label && <div className="text-sm font-semibold text-slate-700 mb-4 text-center" data-id="element-8942">
          {label}
        </div>}

      <div className="flex justify-center items-center overflow-hidden" data-id="element-8943">
        <motion.svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width={svgWidth} height={svgHeight} initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="max-w-full h-auto" data-id="element-8944">
          <g stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4,4" data-id="element-8945">
            <line x1={corners.origin.x} y1={corners.origin.y} x2={corners.backBottomLeft.x} y2={corners.backBottomLeft.y} data-id="element-8946" />
            <line x1={corners.backBottomLeft.x} y1={corners.backBottomLeft.y} x2={corners.backBottomRight.x} y2={corners.backBottomRight.y} data-id="element-8947" />
            <line x1={corners.backBottomLeft.x} y1={corners.backBottomLeft.y} x2={corners.backTopLeft.x} y2={corners.backTopLeft.y} data-id="element-8948" />
          </g>

          <path d={paths.front} fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5" data-id="element-8949" />
          <path d={paths.top} fill="#dbeafe" stroke="#3b82f6" strokeWidth="1.5" data-id="element-8950" />
          <path d={paths.right} fill="#93c5fd" stroke="#3b82f6" strokeWidth="1.5" data-id="element-8951" />

          {showGrid && <g stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.3" data-id="element-8952">
              {gridLines.map(line => <line key={line.key} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} data-id="element-8953" />)}
            </g>}

          <g className="select-none pointer-events-none" data-id="element-8954">
            <text x={labels.length.x} y={labels.length.y} textAnchor="middle" className="text-xs font-bold fill-slate-600 italic" data-id="element-8955">
              L: {length}
              {unit}
            </text>
            <text x={labels.width.x} y={labels.width.y} textAnchor="start" className="text-xs font-bold fill-slate-600 italic" data-id="element-8956">
              B: {width}
              {unit}
            </text>
            <text x={labels.height.x} y={labels.height.y} textAnchor="end" dominantBaseline="middle" className="text-xs font-bold fill-slate-600 italic" data-id="element-8957">
              H: {height}
              {unit}
            </text>
          </g>
        </motion.svg>
      </div>

      {mode === 'support' && <div className="mt-4 pt-4 border-t border-slate-200 text-center" data-id="element-8958">
          <p className="text-sm font-mono text-slate-500" data-id="element-8959">
            <span className="text-blue-600 font-bold" data-id="element-8960">V</span> = {length} ×{' '}
            {width} × {height} ={' '}
            <span className="text-slate-800 font-bold" data-id="element-8961">
              {length * width * height} {unit}³
            </span>
          </p>
        </div>}
    </div>;
}